import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@core/contexts/AuthContext";
import { AuthModal } from "@core/components/modals/AuthModal";
import { useNotifications } from "@core/hooks/useNotifications";
import NotificationBadge from "@core/components/notifications/NotificationBadge";
import NotificationPanel from "@core/components/notifications/NotificationPanel";
import { LanguageSwitcher } from "@core/components/LanguageSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@core/components/ui/dropdown-menu";
import {
  ChevronDown,
  CreditCard,
  FileText,
  Languages,
  LogOut,
  Shield,
  User,
} from "lucide-react";
import {
  getProductConfig,
  getThemeClasses,
  type ProductType,
} from "@core/config/products.config";

interface ProductHeaderProps {
  productType: ProductType;
  showAuth?: boolean;
  showAuthButtons?: boolean;
}

export const ProductHeader: React.FC<ProductHeaderProps> = ({
  productType,
  showAuth = true,
  showAuthButtons = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [authModalOpen, setAuthModalOpen] = React.useState(false);
  const [authMode, setAuthMode] = React.useState<"signin" | "signup">("signin");
  const config = getProductConfig(productType);
  const theme = getThemeClasses(config.theme);
  // Bug #3 fix : on privilégie profile.full_name (source de vérité persistée
  // par le trigger handle_new_user) avant de retomber sur user_metadata (session)
  // puis sur la partie locale de l'email en dernier recours.
  const displayName =
    profile?.full_name ||
    (user?.user_metadata?.full_name as string | undefined) ||
    user?.email?.split("@")[0] ||
    t("default_user_name");

  // Hook des notifications
  const {
    isOpen: isNotificationPanelOpen,
    badgeState,
    creditInfo,
    notifications,
    encouragementMessage,
    togglePanel: toggleNotificationPanel,
    closePanel: closeNotificationPanel,
    dismissNotification,
  } = useNotifications();

  return (
    <>
      <header className="border-b border-violet-900/20 bg-black">
        <div className="flex justify-end items-center h-16 px-2 sm:px-4">
          {showAuth && (
            <div className="flex items-center gap-1.5 sm:gap-3">
              {/* Sélecteur de langue — visible aussi des visiteurs anonymes */}
              <LanguageSwitcher variant="icon" />

              {/* Icône notification avec badge */}
              <NotificationBadge
                badgeState={badgeState}
                onClick={toggleNotificationPanel}
              />

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
                      data-testid="productheader-user-menu-trigger"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-violet-400">
                        <span className="text-sm font-bold text-white">
                          {displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="max-w-28 truncate text-sm">
                        {displayName}
                      </span>
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60">
                    <DropdownMenuLabel className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span className="truncate">{displayName}</span>
                    </DropdownMenuLabel>
                    <DropdownMenuLabel className="truncate pt-0 text-xs font-normal text-gray-500">
                      {user.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate("/account/subscription")}
                      data-testid="productheader-account-subscription"
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t("my_subscription")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/account/invoices")}
                      data-testid="productheader-account-invoices"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      {t("invoices")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/account/security")}
                      data-testid="productheader-account-security"
                    >
                      <Shield className="mr-2 h-4 w-4" />
                      {t("security")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/account/preferences")}
                      data-testid="productheader-account-preferences"
                    >
                      <Languages className="mr-2 h-4 w-4" />
                      {t("preferences")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/data-control")}
                      data-testid="productheader-account-data-control"
                    >
                      <Shield className="mr-2 h-4 w-4" />
                      {t("data_and_privacy")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-500 focus:text-red-500"
                      onClick={async () => {
                        try {
                          await Promise.race([
                            signOut(),
                            new Promise((_, reject) =>
                              setTimeout(
                                () => reject(new Error("signout-timeout")),
                                5000,
                              ),
                            ),
                          ]);
                        } catch {
                          // fall through to forced client-side cleanup
                        }
                        try {
                          localStorage.clear();
                          sessionStorage.clear();
                        } catch {}
                        window.location.assign("/");
                      }}
                      data-testid="productheader-logout-button"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      {t("auth:sign_out")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setAuthMode("signin");
                      setAuthModalOpen(true);
                    }}
                    className={`px-2.5 sm:px-4 py-2 text-xs font-semibold text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap ${theme.authButton} ${theme.authShadow}`}
                    data-testid="productheader-se-connecter"
                  >
                    <span className="xs:hidden">{t("login_short")}</span>
                    <span className="hidden xs:inline">
                      {t("auth:sign_in")}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setAuthMode("signup");
                      setAuthModalOpen(true);
                    }}
                    className={`px-2.5 sm:px-4 py-2 text-xs font-semibold text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap bg-gradient-to-r ${config.headerGradient} hover:opacity-90`}
                    data-testid="productheader-inscription-gratuite"
                  >
                    <span className="xs:hidden">{t("signup_short")}</span>
                    <span className="hidden xs:inline">{t("free_signup")}</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </header>
      <AuthModal
        open={authModalOpen}
        onOpenChange={(open) => setAuthModalOpen(open)}
        defaultMode={authMode}
      />

      {/* Panneau de notifications */}
      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={closeNotificationPanel}
        creditInfo={creditInfo}
        notifications={notifications}
        encouragementMessage={encouragementMessage}
        onDismiss={dismissNotification}
      />
    </>
  );
};
